import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Undo2, SquareDashed, Loader2, Check } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { uploadBase64ToCos } from "../../../utils/fileUtils";

const BLOCK_SIZES = [
  { label: "小", value: 10 },
  { label: "中", value: 20 },
  { label: "大", value: 40 },
];

const BRUSH_SIZES = [
  { label: "细", value: 20 },
  { label: "中", value: 50 },
  { label: "粗", value: 100 },
];

/**
 * 打码编辑弹窗（笔刷涂抹模式）
 * 用户在图片上涂抹进行像素化打码，支持撤销，确认后上传 COS
 */
const MosaicEditorModal = ({ imageUrl, onConfirm, onClose, zIndex = 500, exiting }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const historyRef = useRef([]);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef(null);
  // 保存原始图片像素数据，用于每次重新计算马赛克
  const originalDataRef = useRef(null);
  // 记录当前笔画涂过的区域（canvas 坐标下的 block 集合）
  const strokedBlocksRef = useRef(new Set());

  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [blockSize, setBlockSize] = useState(20);
  const [brushSize, setBrushSize] = useState(50);
  const [canUndo, setCanUndo] = useState(false);

  // 加载图片到 canvas
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      originalDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      historyRef.current = [];
      setCanUndo(false);
      setIsLoaded(true);
    };
    img.onerror = () => toast.error("图片加载失败");
    img.src = imageUrl;
  }, [imageUrl, toast]);

  // 保存快照（一笔开始前）
  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    historyRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    setCanUndo(true);
  }, []);

  // 撤销
  const handleUndo = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || historyRef.current.length === 0) return;
    const prev = historyRef.current.pop();
    const ctx = canvas.getContext("2d");
    ctx.putImageData(prev, 0, 0);
    // 同步更新 originalData 为撤销后的状态，这样后续涂抹基于正确的底图
    originalDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setCanUndo(historyRef.current.length > 0);
  }, []);

  // 对笔刷圆形区域内的 block 做像素化
  const applyMosaicAtBrush = useCallback((cx, cy) => {
    const canvas = canvasRef.current;
    const origData = originalDataRef.current;
    if (!canvas || !origData) return;

    const ctx = canvas.getContext("2d");
    const bs = blockSize;
    const r = brushSize / 2;
    const cw = canvas.width;
    const ch = canvas.height;

    // 计算笔刷覆盖的 block 范围
    const bxMin = Math.max(0, Math.floor((cx - r) / bs));
    const byMin = Math.max(0, Math.floor((cy - r) / bs));
    const bxMax = Math.min(Math.ceil(cw / bs) - 1, Math.floor((cx + r) / bs));
    const byMax = Math.min(Math.ceil(ch / bs) - 1, Math.floor((cy + r) / bs));

    const origPixels = origData.data;
    const currentData = ctx.getImageData(0, 0, cw, ch);
    const pixels = currentData.data;

    let changed = false;

    for (let by = byMin; by <= byMax; by++) {
      for (let bx = bxMin; bx <= bxMax; bx++) {
        // block 中心是否在笔刷圆内
        const blockCx = bx * bs + bs / 2;
        const blockCy = by * bs + bs / 2;
        const dx = blockCx - cx;
        const dy = blockCy - cy;
        if (dx * dx + dy * dy > r * r) continue;

        const key = `${bx},${by}`;
        if (strokedBlocksRef.current.has(key)) continue;
        strokedBlocksRef.current.add(key);
        changed = true;

        // 计算这个 block 在原始图上的平均色
        const px0 = bx * bs;
        const py0 = by * bs;
        const px1 = Math.min(px0 + bs, cw);
        const py1 = Math.min(py0 + bs, ch);
        let rr = 0, gg = 0, bb = 0, count = 0;
        for (let py = py0; py < py1; py++) {
          for (let px = px0; px < px1; px++) {
            const idx = (py * cw + px) * 4;
            rr += origPixels[idx];
            gg += origPixels[idx + 1];
            bb += origPixels[idx + 2];
            count++;
          }
        }
        rr = Math.round(rr / count);
        gg = Math.round(gg / count);
        bb = Math.round(bb / count);

        // 填充 block
        for (let py = py0; py < py1; py++) {
          for (let px = px0; px < px1; px++) {
            const idx = (py * cw + px) * 4;
            pixels[idx] = rr;
            pixels[idx + 1] = gg;
            pixels[idx + 2] = bb;
          }
        }
      }
    }

    if (changed) {
      ctx.putImageData(currentData, 0, 0);
    }
  }, [blockSize, brushSize]);

  // 在两点之间插值涂抹（避免快速移动时断线）
  const strokeLine = useCallback((from, to) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const step = Math.max(blockSize / 2, 4);
    const steps = Math.max(1, Math.ceil(dist / step));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      applyMosaicAtBrush(from.x + dx * t, from.y + dy * t);
    }
  }, [applyMosaicAtBrush, blockSize]);

  // 鼠标坐标 → canvas 坐标
  const toCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (!isLoaded) return;
    saveSnapshot();
    // 保存当前画布像素作为这一笔的 originalData
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      originalDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    }
    strokedBlocksRef.current = new Set();
    isDrawingRef.current = true;
    const pos = toCanvasCoords(e);
    lastPosRef.current = pos;
    applyMosaicAtBrush(pos.x, pos.y);
  }, [isLoaded, toCanvasCoords, saveSnapshot, applyMosaicAtBrush]);

  const handleMouseMove = useCallback((e) => {
    if (!isDrawingRef.current) return;
    const pos = toCanvasCoords(e);
    if (lastPosRef.current) {
      strokeLine(lastPosRef.current, pos);
    }
    lastPosRef.current = pos;
  }, [toCanvasCoords, strokeLine]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPosRef.current = null;
  }, []);

  // 确认保存
  const handleConfirm = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsUploading(true);
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const cosUrl = await uploadBase64ToCos(dataUrl);
      onConfirm(cosUrl);
    } catch (e) {
      toast.error("上传失败: " + e.message);
    } finally {
      setIsUploading(false);
    }
  }, [onConfirm, toast]);

  // 笔刷光标样式
  const cursorSize = (() => {
    const canvas = canvasRef.current;
    if (!canvas) return brushSize;
    const rect = canvas.getBoundingClientRect();
    return brushSize * (rect.width / canvas.width);
  })();

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}
      style={{ backgroundColor: "var(--overlay-heavy)", zIndex }}
      onClick={onClose}
    >
      <div
        className={`relative rounded-2xl shadow-2xl overflow-hidden flex flex-col ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border-secondary)",
          width: "min(900px, 92vw)",
          maxHeight: "90vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--color-border-secondary)" }}
        >
          <div className="flex items-center gap-3">
            <SquareDashed size={16} style={{ color: theme.colors.accentPrimary }} />
            <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
              打码编辑
            </span>
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              涂抹区域进行像素化打码
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-muted)" }}
          >
            <X size={16} />
          </button>
        </div>

        {/* 工具栏 */}
        <div
          className="flex items-center justify-between px-5 py-2"
          style={{ borderBottom: "1px solid var(--color-border-secondary)" }}
        >
          <div className="flex items-center gap-5">
            {/* 马赛克粒度 */}
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>粒度</span>
              {BLOCK_SIZES.map((bs) => (
                <button
                  key={bs.value}
                  onClick={() => setBlockSize(bs.value)}
                  className="px-2 py-0.5 rounded text-xs font-medium transition-all"
                  style={{
                    backgroundColor: blockSize === bs.value ? theme.colors.accentPrimary : "var(--color-bg-tertiary)",
                    color: blockSize === bs.value ? "var(--color-accent-contrast)" : "var(--color-text-secondary)",
                  }}
                >
                  {bs.label}
                </button>
              ))}
            </div>
            {/* 笔刷大小 */}
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>笔刷</span>
              {BRUSH_SIZES.map((bs) => (
                <button
                  key={bs.value}
                  onClick={() => setBrushSize(bs.value)}
                  className="px-2 py-0.5 rounded text-xs font-medium transition-all"
                  style={{
                    backgroundColor: brushSize === bs.value ? theme.colors.accentPrimary : "var(--color-bg-tertiary)",
                    color: brushSize === bs.value ? "var(--color-accent-contrast)" : "var(--color-text-secondary)",
                  }}
                >
                  {bs.label}
                </button>
              ))}
            </div>
            {/* 撤销 */}
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: canUndo ? "var(--color-text-primary)" : "var(--color-text-muted)", opacity: canUndo ? 1 : 0.4 }}
            >
              <Undo2 size={13} /> 撤销
            </button>
          </div>
        </div>

        {/* 画布区域 */}
        <div className="flex-1 overflow-auto px-5 py-4 flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
          <div
            className="relative inline-block"
            style={{
              cursor: isLoaded
                ? `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${Math.round(cursorSize)}' height='${Math.round(cursorSize)}'><circle cx='${Math.round(cursorSize / 2)}' cy='${Math.round(cursorSize / 2)}' r='${Math.round(cursorSize / 2 - 1)}' fill='none' stroke='white' stroke-width='1.5' opacity='0.7'/></svg>") ${Math.round(cursorSize / 2)} ${Math.round(cursorSize / 2)}, crosshair`
                : "default",
            }}
          >
            <canvas
              ref={canvasRef}
              className="block"
              style={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain" }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 size={24} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
              </div>
            )}
          </div>
        </div>

        {/* 底部操作栏 */}
        <div
          className="flex items-center justify-end gap-3 px-5 py-3"
          style={{ borderTop: "1px solid var(--color-border-secondary)" }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-secondary)" }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={isUploading || !isLoaded}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: isUploading ? `${theme.colors.accentPrimary}60` : theme.colors.accentPrimary,
              color: "var(--color-accent-contrast)",
              opacity: !isLoaded ? 0.5 : 1,
            }}
          >
            {isUploading ? (
              <><Loader2 size={14} className="animate-spin" /> 上传中...</>
            ) : (
              <><Check size={14} /> 确认打码</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(MosaicEditorModal);
